import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Button,
    Box,
    IconButton,
    Grid
} from '@mui/material';
import { ArrowBack, ArrowForward, Language } from '@mui/icons-material';
import image from "../../assets/images"

const modalContents = [
    {
        title: "Welcome to Berhu",
        titleEs: "Bienvenido a Berhu",
        text: "Welcome to Berhu, your AI-powered assistant! Our platform is designed to help you choose and customize AI agents tailored to your professional needs, whether you're a business owner, entrepreneur, or student. In this guide, you’ll learn how to navigate the app, interact with agents, and make the most out of its features.",
        textEs: "¡Bienvenido a Berhu, tu asistente impulsado por IA! Nuestra plataforma está diseñada para ayudarte a elegir y personalizar agentes de IA adaptados a tus necesidades profesionales, ya seas dueño de un negocio, emprendedor o estudiante. En esta guía, aprenderás a navegar por la aplicación, interactuar con los agentes y sacar el máximo provecho de sus funciones.",
        image: image.Variante0
    },
    {
        title: "Your journey starts here",
        titleEs: "Tu viaje comienza aquí",
        text: "Your journey starts here. The Main Dashboard serves as the control center where you can view agent activities and performance metrics. From here, you can access key functions like chat, agent selection, and personalization. The dashboard icon will guide you back to this screen from anywhere within the app.",
        textEs: "Tu viaje comienza aquí. El Tablero Principal actúa como el centro de control donde puedes ver las actividades de los agentes y las métricas de rendimiento. Desde aquí, puedes acceder a funciones clave como chat, selección de agentes y personalización. El icono del tablero te llevará de vuelta a esta pantalla desde cualquier lugar de la aplicación.",
        image: image.Variante1
    },
    {
        title: "Choosing the right agent",
        titleEs: "Eligiendo el agente adecuado",
        text: "Choosing the right agent is crucial in Berhu. Each agent is a specialist in a particular area, such as business management, entrepreneurial support, or academic assistance. The Agent Selection menu allows you to explore profiles, each represented by a unique icon. Read short descriptions to choose the agent that best fits your needs.",
        textEs: "Elegir el agente adecuado es crucial en Berhu. Cada agente es especialista en un área particular, como gestión empresarial, apoyo emprendedor o asistencia académica. El menú de Selección de Agentes te permite explorar los perfiles, cada uno representado por un ícono único. Lee las descripciones breves para elegir el agente que mejor se ajuste a tus necesidades.",
        image: image.Variante2
    },
    {
        title: "Interacting with your AI agent",
        titleEs: "Interactuando con tu agente de IA",
        text: "Interacting with your AI agent is simple through the integrated Chat function. Tap the chat bubble icon to initiate a conversation with your selected agent. Ask questions, receive suggestions, and fine-tune the agent’s approach based on your needs. Your agent is ready to assist you in real time!",
        textEs: "Interactuar con tu agente de IA es sencillo a través de la función de Chat integrado. Pulsa el icono de la burbuja de chat para iniciar una conversación con el agente seleccionado. Haz preguntas, recibe sugerencias y ajusta el enfoque del agente según tus necesidades. ¡Tu agente está listo para asistirte en tiempo real!",
        image: image.Variante3
    },
    {
        title: "Customizing your agent",
        titleEs: "Personalizando tu agente",
        text: "Want your agent to be even more tailored to your style? Through the Customization section, you can tweak the agent’s behavior and responses. Use the gear icon to access this feature and make adjustments that enhance your experience. Make your agent as unique as you!",
        textEs: "¿Quieres que tu agente se adapte aún más a tu estilo? A través de la sección de Personalización, puedes ajustar el comportamiento y las respuestas del agente. Usa el ícono de engranaje para acceder a esta función y realizar ajustes que mejoren tu experiencia. ¡Haz que tu agente sea tan único como tú!",
        image: image.Variante4
    },
    {
        title: "It’s time to interact with your agent",
        titleEs: "¡Es hora de interactuar con tu agente!",
        text: "It’s time to interact with your agent! Use the Chat to start asking for what you need. Simply select the right agent for you and open the chat by clicking on the chat bubble icon. Ask questions, request recommendations, or get help with what you're working on. Your agent is ready to collaborate with you and boost your productivity!",
        textEs: "¡Es hora de interactuar con tu agente! Usa el chat para comenzar a pedir lo que necesitas. Simplemente selecciona el agente adecuado para ti y abre el chat haciendo clic en el icono de la burbuja de chat. Haz preguntas, pide recomendaciones o solicita ayuda con lo que estés trabajando. ¡Tu agente está listo para colaborar contigo y aumentar tu productividad!",
        image: image.Variante5
    }
];


interface BerIuModalProps {
    open: boolean;
    onClose: () => void;
}

export default function BerIuModal({ open, onClose }: BerIuModalProps) {
    const [currentModal, setCurrentModal] = useState(0);
    const [language, setLanguage] = useState('en');

    const handleNext = () => setCurrentModal((prev) => (prev + 1) % modalContents.length);
    const handlePrev = () => setCurrentModal((prev) => (prev - 1 + modalContents.length) % modalContents.length);
    const toggleLanguage = () => setLanguage((prev) => prev === 'en' ? 'es' : 'en');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '1200px',
                    height: '800px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 2,
                    position: 'relative',  // Para posicionar el contador en la esquina superior derecha
                },
            }}
        >
            <DialogContent
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 2,
                }}
            >
                {/* Contador de modal */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        padding: '4px 8px',
                        boxShadow: 2,
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        {currentModal + 1} / {modalContents.length}
                    </Typography>
                </Box>

                {/* Logo */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <img src={image.LogoModales} alt="Ber-iu Logo" style={{ maxWidth: '200px', height: 'auto' }} />
                </Box>

                {/* Contenido del modal */}
                <Grid container spacing={2} sx={{ flexGrow: 1, height: '100%' }}>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            height: '100%',
                        }}
                    >
                        <Typography variant="h4" gutterBottom>
                            {language === 'en' ? modalContents[currentModal].title : modalContents[currentModal].titleEs}
                        </Typography>
                        <Typography variant="body1" sx={{ textAlign: 'justify' }}>
                            {language === 'en' ? modalContents[currentModal].text : modalContents[currentModal].textEs}
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <img
                            src={modalContents[currentModal].image}
                            alt="Modal illustration"
                            style={{ maxWidth: '100%', height: 'auto', maxHeight: '90%' }}
                        />
                    </Grid>
                </Grid>

                {/* Controles de navegación */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <IconButton onClick={handlePrev} aria-label="Previous">
                        <ArrowBack />
                    </IconButton>
                    <Button onClick={toggleLanguage} startIcon={<Language />}>
                        {language === 'en' ? 'Español' : 'English'}
                    </Button>
                    <IconButton onClick={handleNext} aria-label="Next">
                        <ArrowForward />
                    </IconButton>
                </Box>
            </DialogContent>
        </Dialog>

    );
}
